import { Timestamp } from "api/firebase";
import orderService from "api/order";
import groupBy from "lodash.groupby";
import {
  BillingDetails,
  CartTotalAmount,
} from "store/exhibitors/cartStepper/helper";
import separateProductsWithApproval from "tools/product/separateProductsWithApproval";
import { PaymentMethodType } from "ts/api/paymentMethod";
import { FullProduct } from "ts/api/product";

import composeIdealOrder from "view/apps/Exhibitor/pages/Cart/helpers/composeOrderFileds/composers/composeIdealOrder";
import { ComposeOrderBaseParams } from "view/apps/Exhibitor/pages/Cart/helpers/composeOrderFileds/types";
import { IOrganiser, IShow } from "@evennt-tech/api-v2-core";
import { v4 as uuid } from "uuid";
import { BankTransferBillingDetails } from "store/exhibitors/cartStepper/slice";
import composeCardOrder from "../composeOrderFileds/composers/composeCardOrder";
import composeBankTransferOrder from "../composeOrderFileds/composers/composeBankTransferOrder";
import { CreatedOrdersResult } from "../../components/types";

type CreateOrderParams = {
  products: FullProduct[];
  standId: string;
  show: IShow;
  exhibitorPairId: string;
  organiser: IOrganiser;
  exhibitorId: string;
  transferredPaymentDescription: string;
  billingDetails: BillingDetails;
  totalAmount: CartTotalAmount;
  poNumbers?: Record<string, string>;
  paymentMethodType?: PaymentMethodType;
  isDraft?: boolean;
  quickOrderId?: string | null;
  billingMetadata?: BankTransferBillingDetails;
  allowBankTransfer?: boolean;
};

const createOrders = async ({
  products,
  standId,
  exhibitorId,
  billingDetails,
  totalAmount,
  show,
  organiser,
  transferredPaymentDescription,
  poNumbers,
  paymentMethodType,
  isDraft,
  quickOrderId,
  exhibitorPairId,
  billingMetadata,
  allowBankTransfer,
}: CreateOrderParams) => {
  const {
    productsWithApproval,
    productsWithoutApproval,
  } = separateProductsWithApproval(products);
  const createdAt = Timestamp.now();
  const orderGroupId = uuid();
  const orders: CreatedOrdersResult = {
    ordersWithApproval: [],
    orderWithoutApproval: null,
    groupId: null,
  };
  let orderCount = 0;
  const generateOrderId = () => {
    const base = Date.now();
    orderCount = (orderCount + 1) % 100;
    const suffix = orderCount.toString().padStart(2, "0");
    return `${base}${suffix}`;
  };
  if (productsWithApproval?.length) {
    // NOTE: There is no way to pay via iDEAL
    if (paymentMethodType === PaymentMethodType.cardPayment) {
      const productsByCompany = groupBy(productsWithApproval, "owner.id");
      for await (const [supplierId, products] of Object.entries(
        productsByCompany
      )) {
        const orderId = generateOrderId();
        const amount = totalAmount?.withApproval?.[supplierId];
        if (amount === undefined) {
          // NOTE: No idea why we should break;
          break;
        }

        const cardOrder = await composeCardOrder({
          paymentType: PaymentMethodType.cardPayment,
          organiser,
          exhibitorPairId,
          transferredPaymentDescription,
          show,
          products,
          standId,
          exhibitorId,
          billingDetails,
          totalAmount: amount,
          createdAt,
          isRequiredApproval: true,
          id: orderId,
          orderGroupId,
        });
        if (!cardOrder) {
          break;
        }

        if (!isDraft) {
          await orderService.addItem(show.id, {
            ...cardOrder,
            isQuick: !!quickOrderId,
          });
        }
        orders.ordersWithApproval.push(cardOrder);
        orders.transfersWithApproval = {
          ...(orders.transfersWithApproval || {}),
          [orderId]: cardOrder.transfers,
        };
      }

      orders.groupId = orderGroupId;
    }
    if (paymentMethodType === PaymentMethodType.bankTransfer) {
      orders.groupId = orderGroupId;
      const baseOrderFields: Omit<
        ComposeOrderBaseParams,
        "id" | "paymentType" | "products" | "totalAmount"
      > = {
        exhibitorPairId,
        createdAt,
        exhibitorId,
        standId,
        billingDetails,
        orderGroupId,
      };
      const productsByCompany = groupBy(productsWithApproval, "owner.id");
      for await (const [supplierId, products] of Object.entries(
        productsByCompany
      )) {
        const orderId = generateOrderId();
        const amount = totalAmount?.withApproval?.[supplierId];
        if (!amount) {
          // NOTE: No idea why we should break;
          break;
        }
        const bankTransferOrder = await composeBankTransferOrder({
          ...baseOrderFields,
          exhibitorPairId,
          products,
          totalAmount: amount,
          organiser,
          show,
          transferredPaymentDescription,
          paymentType: PaymentMethodType.bankTransfer,
          poNumbers: poNumbers || {},
          isRequiredApproval: true,
          id: orderId,
          billingMetadata: billingMetadata as BankTransferBillingDetails,
        });

        if (!isDraft) {
          await orderService.addItem(show.id, {
            ...bankTransferOrder,
            isQuick: !!quickOrderId,
          });
        }

        orders.invoicesWithApproval = {
          ...(orders.invoicesWithApproval || {}),
          [orderId]: bankTransferOrder.invoices,
        };
        orders.ordersWithApproval.push(bankTransferOrder);
      }
    }
  }
  if (productsWithoutApproval?.length) {
    const orderId = quickOrderId || generateOrderId();
    const baseOrderFields: Omit<ComposeOrderBaseParams, "paymentType"> = {
      createdAt,
      exhibitorId,
      exhibitorPairId,
      standId,
      billingDetails,
      products: productsWithoutApproval,
      totalAmount: totalAmount.withoutApproval,
      id: orderId,
      isQuick: !!quickOrderId,
      allowBankTransfer: !!quickOrderId && !!allowBankTransfer,
      orderGroupId,
    };

    if (paymentMethodType === PaymentMethodType.cardPayment) {
      const cardOrder = await composeCardOrder({
        ...baseOrderFields,
        paymentType: PaymentMethodType.cardPayment,
        show,
        organiser,
        exhibitorPairId,
        transferredPaymentDescription,
      });
      if (!isDraft) {
        await orderService.addItem(show.id, {
          ...cardOrder,
          isQuick: !!quickOrderId,
        });
      }
      orders.orderWithoutApproval = cardOrder;
      orders.transfersWithoutApproval = cardOrder.transfers;
    }
    if (paymentMethodType === PaymentMethodType.bankTransfer) {
      const bankTransferOrder = await composeBankTransferOrder({
        ...baseOrderFields,
        exhibitorPairId,
        paymentType: PaymentMethodType.bankTransfer,
        organiser,
        show,
        transferredPaymentDescription,
        poNumbers: poNumbers || {},
        id: orderId,
        billingMetadata: billingMetadata as BankTransferBillingDetails,
      });
      if (!isDraft) {
        await orderService.addItem(show.id, {
          ...bankTransferOrder,
          isQuick: !!quickOrderId,
        });
      }

      orders.orderWithoutApproval = bankTransferOrder;
      orders.invoicesWithoutApproval = bankTransferOrder.invoices;
    }
    if (paymentMethodType === PaymentMethodType.idealPayment) {
      const idealOrder = await composeIdealOrder({
        ...baseOrderFields,
        exhibitorPairId,
        paymentType: PaymentMethodType.idealPayment,
        organiser,
        show,
        transferredPaymentDescription,
        id: orderId,
      });
      if (!isDraft) {
        await orderService.addItem(show.id, {
          ...idealOrder,
          isQuick: !!quickOrderId,
        });
      }

      orders.orderWithoutApproval = idealOrder;
      orders.transfersWithoutApproval = idealOrder.transfers;
    }
  }

  return orders;
};

export default createOrders;
